import { useRef } from "react";
import type { ArrowCorner, ArrowEdge, ShapeCanvasObject } from "@refinement-canvas/domain";
import { resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { resolveArrowEdgePath, resolveArrowPath } from "../../../lib/shape-arrow";
import { resolveRectCornerRadius } from "../../../lib/shape-rect";
import { SHAPE_DEFAULT_STROKE, SHAPE_DEFAULT_STROKE_WIDTH } from "../../../lib/shape-preference";
import { resolveStrokePattern, strokeDashArray } from "../../../lib/stroke-pattern";

interface Props {
  readonly object: ShapeCanvasObject;
}

// SVG 描画上の矢じりサイズ（軸方向の長さ / 直交方向の片側張り出し）。
const ARROW_HEAD_LENGTH = 12;
const ARROW_HEAD_HALF = 6;

// 図形オブジェクト（丸 / 四角 / 矢印）。SVG で描画する。
// クリックで選択、ドラッグで移動できる。SVG 自体に pointer events を付けて
// 内側 fill が transparent でもヒットを取れるよう pointerEvents="all" を付与する。
export function ShapeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // 図形の輪郭色は常に自前の色（選択で色は変えない）。選択のハイライトは他オブジェクト種別と同様、
  // SVG のボックス（= object.w×h の AABB）へ outline を描いて示す。これにより丸 / 矢印でも選択枠が
  // 図形サイズに一致し、リサイズ / 回転ハンドルとも枠でつながって見える（要件⑤）。
  const stroke = object.stroke ?? SHAPE_DEFAULT_STROKE;
  // 永続スキーマは 1px 未満を拒否するが、不正な旧データが楽観 state へ残っても線を失わないよう下限を設ける。
  const strokeWidth = Math.max(0.5, object.strokeWidth ?? SHAPE_DEFAULT_STROKE_WIDTH);

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="shape"
      // ブラウザのネイティブドラッグに pointer capture を奪われないようにする。
      {...{ draggable: false }}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        // ドラッグ開始時にテキスト選択が割り込まないようにする。
        userSelect: "none",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        // 参照専用では動かせないため default。配置ツール・ペン描画の選択中は、既存オブジェクトの上でも新規作成（crosshair）であることを示す。
        // move のままだと「移動できる」と誤認させるが、実際は pointerdown が stage 配置 / ペン描画へ譲られる。
        cursor: resolveObjectCursor({ readOnly, activeTool }),
        overflow: "visible",
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする
        // （image / text / sticky 等と統一）。overflow:visible のため outline は AABB の縁に乗る。
        outline: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "none",
      }}
      // リサイズ中に viewBox が更新されない瞬間でも、内部要素を常に SVG の表示寸法へ
      // 等比展開させたい。preserveAspectRatio="none" で縦横独立に伸縮し、
      // viewBox の値に依存せずドロップ後の最終図形と同じ見た目をプレビューする。
      viewBox={`0 0 ${object.w} ${object.h}`}
      preserveAspectRatio="none"
    >
      <ShapeGlyph
        shapeKind={object.shapeKind}
        w={object.w}
        h={object.h}
        fill={object.fill ?? "transparent"}
        stroke={stroke}
        strokePattern={object.strokePattern}
        strokeWidth={strokeWidth}
        arrowFromEdge={object.arrowFromEdge}
        arrowFromCorner={object.arrowFromCorner}
        // ペン描画中は内側の図形要素もヒット対象から外し、図形の上をペンが素通りできるようにする
        // （SVG ルートは pointerEvents:none だが、内側 glyph が all のままだとポインタを奪い描画を妨げるため）。
        pointerEvents={activeTool === "draw-pen" ? "none" : "all"}
      />
    </svg>
  );
}

/**
 * 図形の内側描画要素（丸 / 四角 / 矢印）。
 * 確定済みオブジェクト（ShapeObject）とドラッグ配置中のプレビュー（CanvasShapePlacementOverlay）で
 * 共有し、見た目を一致させる。座標はすべて呼び出し側の SVG（viewBox 0 0 w h）に追従する。
 */
export function ShapeGlyph({
  shapeKind,
  w,
  h,
  fill,
  stroke,
  strokePattern,
  strokeWidth,
  arrowFromEdge,
  arrowFromCorner,
  pointerEvents = "all",
}: {
  readonly shapeKind: ShapeCanvasObject["shapeKind"];
  readonly w: number;
  readonly h: number;
  readonly fill: string;
  readonly stroke: string;
  readonly strokePattern?: ShapeCanvasObject["strokePattern"] | undefined;
  readonly strokeWidth: number;
  readonly arrowFromEdge?: ArrowEdge | undefined;
  readonly arrowFromCorner?: ArrowCorner | undefined;
  readonly pointerEvents?: "all" | "none" | undefined;
}): JSX.Element {
  const pattern = resolveStrokePattern(strokePattern);
  const dashArray = strokeDashArray(strokeWidth, pattern);
  const dashedProps =
    pattern === "dotted"
      ? { strokeDasharray: dashArray.join(" "), strokeLinecap: "round" as const }
      : {};
  if (shapeKind === "circle") {
    // 100% 基準で SVG 表示寸法に追従させ、ドラッグ中の枠と中身を一致させる。
    // stroke は vector-effect で非スケールに固定し、伸縮しても太さが均一に保たれる。
    return (
      <ellipse
        cx="50%"
        cy="50%"
        rx="50%"
        ry="50%"
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        {...dashedProps}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      />
    );
  }
  if (shapeKind === "rectangle") {
    // 矩形も 100% で SVG を埋めて、リサイズ中の輪郭が選択枠と一致するようにする。
    return (
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        rx={resolveRectCornerRadius(w, h)}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        {...dashedProps}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      />
    );
  }
  // 軸そろえ矢印は起点辺→対辺、対角矢印は起点角→対角へ描く。
  // どちらも未指定（旧データ・MCP 作成）は従来どおり水平左→右で描く。
  // preserveAspectRatio="none" 下で viewBox を等比伸縮するため、矢じり先端の絶対サイズが
  // 多少伸縮するが、ドロップ後の最終形に近いゴースト表示となる。
  if (arrowFromEdge || arrowFromCorner) {
    const path = arrowFromEdge
      ? resolveArrowEdgePath(w, h, arrowFromEdge, ARROW_HEAD_LENGTH, ARROW_HEAD_HALF)
      : resolveArrowPath(w, h, arrowFromCorner!, ARROW_HEAD_LENGTH, ARROW_HEAD_HALF);
    return (
      <g
        stroke={stroke}
        fill="none"
        strokeWidth={strokeWidth}
        {...dashedProps}
        vectorEffect="non-scaling-stroke"
        pointerEvents={pointerEvents}
      >
        <line x1={path.tail.x} y1={path.tail.y} x2={path.head.x} y2={path.head.y} />
        <polyline
          points={`${path.wingA.x},${path.wingA.y} ${path.head.x},${path.head.y} ${path.wingB.x},${path.wingB.y}`}
        />
      </g>
    );
  }
  return (
    <g
      stroke={stroke}
      fill="none"
      strokeWidth={strokeWidth}
      {...dashedProps}
      vectorEffect="non-scaling-stroke"
      pointerEvents={pointerEvents}
    >
      <line x1={4} y1={h / 2} x2={w - 12} y2={h / 2} />
      <polyline
        points={`${w - 12},${h / 2 - 6} ${w - 4},${h / 2} ${w - 12},${h / 2 + 6}`}
      />
    </g>
  );
}
